import React, { useMemo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, Wrench, BarChart, Download, FileText, Eye, Printer, Package as PackageIcon, ChevronDown, TrendingUp, Users, Activity, DollarSign } from 'lucide-react';
import { Report, ReportType, WorkflowRequest } from '../../types';
import { useAppContext } from '../../hooks/useAppContext';
import useAppStore from '../../store/useAppStore';
import { API_BASE_URL } from '../../config';
import { Pagination } from '../../components/common/Pagination';

// Helper function for precise time ago formatting
const timeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (seconds < 30) return `منذ بضع ثوان`;
    let interval = seconds / 31536000;
    if (interval > 1) return `منذ ${Math.floor(interval)} سنوات`;
    interval = seconds / 2592000;
    if (interval > 1) return `منذ ${Math.floor(interval)} أشهر`;
    interval = seconds / 86400;
    if (interval > 1) return `منذ ${Math.floor(interval)} أيام`;
    interval = seconds / 3600;
    if (interval > 1) return `منذ ${Math.floor(interval)} ساعات`;
    interval = seconds / 60;
    if (interval > 1) return `منذ ${Math.floor(interval)} دقائق`;
    return `منذ ${Math.floor(seconds)} ثوان`;
};

const ModernStatCard: React.FC<{
    title: string;
    count: number;
    icon: React.ElementType;
    color: string;
    delay: number;
    onClick?: () => void;
}> = ({ title, count, icon: Icon, color, delay, onClick }) => (
    <div
        onClick={onClick}
        className={`glass-card p-6 rounded-2xl relative overflow-hidden group cursor-pointer animate-fade-in-up`}
        style={{ animationDelay: `${delay}ms` }}
    >
        <div className={`absolute -right-6 -top-6 w-24 h-24 bg-${color}-500/20 rounded-full blur-2xl group-hover:bg-${color}-500/30 transition-colors`}></div>

        <div className="relative z-10 flex justify-between items-start">
            <div>
                <p className="text-slate-400 text-sm font-medium mb-1">{title}</p>
                <h3 className="text-3xl font-bold text-white group-hover:scale-110 transition-transform origin-right">{count}</h3>
            </div>
            <div className={`p-3 rounded-xl bg-${color}-500/10 text-${color}-400 group-hover:text-white group-hover:bg-${color}-500 transition-all duration-300`}>
                <Icon size={24} />
            </div>
        </div>

        <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
            <span className="text-emerald-400 flex items-center gap-1">
                <TrendingUp size={12} />
                +2.5%
            </span>
            <span>من الشهر الماضي</span>
        </div>
    </div>
);

// Minimal types
interface PackageRequest { id: string; title?: string; employeeName?: string; creationDate?: string; }
interface PurchaseItem { id: string; invoiceNumber: string; vendor?: string; employeeName?: string; invoiceDate?: string; }
interface QuotationSummary { id: string; customer_name: string; quote_number: string; quote_date: string; total_with_tax: number; created_at?: string; }

const ModernAdminDashboardScreen: React.FC = () => {
    const { t, user } = useAppContext();
    const { reports, requests, printReport } = useAppStore();
    const navigate = useNavigate();

    const [packageRequests, setPackageRequests] = useState<PackageRequest[]>([]);
    const [purchaseItems, setPurchaseItems] = useState<PurchaseItem[]>([]);
    const [quotationSummaries, setQuotationSummaries] = useState<QuotationSummary[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch Packages
                const pkgRes = await fetch(`${API_BASE_URL}/package-requests`, {
                    headers: user ? { 'X-User-Id': user.id, 'X-User-Role': user.role } : {}
                });
                const pkgData = await pkgRes.json().catch(() => []);
                setPackageRequests(Array.isArray(pkgData) ? pkgData.map((p: any) => ({
                    id: p.id, title: p.title, employeeName: p.employeeName, creationDate: p.creationDate
                })) : []);

                // Fetch Quotations
                const qtRes = await fetch(`${API_BASE_URL}/quotations?limit=12`);
                const qtData = await qtRes.json().catch(() => []);
                const qtItems = Array.isArray(qtData) ? qtData : (Array.isArray((qtData as any).items) ? (qtData as any).items : []);
                setQuotationSummaries(qtItems.map((q: any) => ({
                    id: String(q.id), customer_name: String(q.customer_name || ''), quote_number: String(q.quote_number || ''),
                    quote_date: String(q.quote_date || ''), total_with_tax: Number(q.total_with_tax || 0), created_at: String(q.created_at || '')
                })));

                // Load Purchases
                const rawPurch = localStorage.getItem('purchase_archive_items');
                const purchData = rawPurch ? JSON.parse(rawPurch) : [];
                setPurchaseItems(Array.isArray(purchData) ? purchData.map((p: any) => ({
                    id: String(p.id), invoiceNumber: String(p.invoiceNumber || ''), vendor: p.vendor, employeeName: p.employeeName, invoiceDate: p.invoiceDate
                })) : []);

            } catch (err) {
                console.error("Dashboard data fetch error", err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [user]);

    const sortedReports = useMemo(() => [...reports].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()), [reports]);

    const stats = useMemo(() => ({
        sales: sortedReports.filter(r => r.type === ReportType.Sales).length,
        maintenance: sortedReports.filter(r => r.type === ReportType.Maintenance).length,
        projects: sortedReports.filter(r => r.type === ReportType.Project).length,
        workflows: requests.length,
        packages: packageRequests.length,
        quotations: quotationSummaries.length,
    }), [sortedReports, requests, packageRequests, quotationSummaries]);

    const recentUnified = useMemo(() => {
        const reportItems = sortedReports.map(r => ({
            id: r.id, kind: 'report' as const, title: `تقرير ${r.type}`, subtitle: r.employeeName,
            date: new Date(r.date), rType: r.type, icon: FileText, color: 'blue'
        }));
        const workflowItems = requests.map(req => ({
            id: req.id, kind: 'workflow' as const, title: req.title || 'طلب سير عمل', subtitle: req.stageHistory[0]?.processor || 'غير معروف',
            date: new Date(req.creationDate), icon: Download, color: 'purple'
        }));
        const packageItems = packageRequests.map(p => ({
            id: p.id, kind: 'package' as const, title: p.title || 'طلب بكج', subtitle: p.employeeName,
            date: new Date(p.creationDate || 0), icon: PackageIcon, color: 'orange'
        }));
        const quotationItems = quotationSummaries.map(q => ({
            id: q.id, kind: 'quotation' as const, title: q.customer_name, subtitle: q.quote_number,
            date: new Date(q.quote_date || q.created_at || 0), icon: FileText, color: 'emerald'
        }));

        return [...reportItems, ...workflowItems, ...packageItems, ...quotationItems]
            .sort((a, b) => b.date.getTime() - a.date.getTime());
    }, [sortedReports, requests, packageRequests, quotationSummaries]);

    const [filter, setFilter] = useState('all');
    const filteredItems = useMemo(() => {
        if (filter === 'all') return recentUnified;
        return recentUnified.filter(i => i.kind === filter || (i.kind === 'report' && 'rType' in i && i.rType === filter)); // Simplified filter logic
    }, [recentUnified, filter]);

    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 8;
    const paginatedItems = filteredItems.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

    return (
        <div className="space-y-8 animate-content-fade-in">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold modern-gradient-text mb-1">{t('adminDashboard')}</h1>
                    <p className="text-slate-400">نظرة عامة على أداء النظام والعمليات</p>
                </div>
                <div className="flex gap-2">
                    <button className="px-4 py-2 rounded-xl bg-slate-800 text-slate-300 hover:bg-slate-700 transition-colors text-sm font-medium">تصدير التقرير</button>
                    <button className="px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-500 transition-colors text-sm font-medium shadow-lg shadow-blue-500/20">تحديث البيانات</button>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <ModernStatCard title="المبيعات" count={stats.sales} icon={BarChart} color="blue" delay={0} onClick={() => setFilter('sales')} />
                <ModernStatCard title="المشاريع" count={stats.projects} icon={Briefcase} color="orange" delay={100} onClick={() => navigate('/admin-projects')} />
                <ModernStatCard title="الصيانة" count={stats.maintenance} icon={Wrench} color="emerald" delay={200} onClick={() => setFilter('maintenance')} />
                <ModernStatCard title="عروض السعر" count={stats.quotations} icon={FileText} color="rose" delay={300} onClick={() => navigate('/admin-quotations')} />
            </div>

            {/* Main Content Area */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Recent Activity Feed */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="glass-panel rounded-2xl p-6 min-h-[600px] flex flex-col">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold text-white flex items-center gap-2">
                                <Activity size={20} className="text-blue-400" />
                                النشاطات الأخيرة
                            </h2>
                            <div className="flex gap-2">
                                {['all', 'report', 'workflow', 'package'].map(f => (
                                    <button
                                        key={f}
                                        onClick={() => setFilter(f)}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filter === f ? 'bg-blue-500/20 text-blue-400 ring-1 ring-blue-500/50' : 'bg-slate-800/50 text-slate-400 hover:bg-slate-800'}`}
                                    >
                                        {f === 'all' ? 'الكل' : f === 'report' ? 'التقارير' : f === 'workflow' ? 'العمليات' : 'البكجات'}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="flex-1 space-y-3">
                            {paginatedItems.map((item, idx) => (
                                <div
                                    key={`${item.kind}-${item.id}`}
                                    className="group flex items-center justify-between p-4 rounded-xl bg-slate-800/30 hover:bg-slate-800/60 border border-slate-700/30 hover:border-slate-600 transition-all duration-300"
                                    style={{ animationDelay: `${idx * 50}ms` }}
                                >
                                    <div className="flex items-center gap-4">
                                        <div className={`p-3 rounded-full bg-${item.color}-500/10 text-${item.color}-400 group-hover:scale-110 transition-transform`}>
                                            <item.icon size={18} />
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-slate-200 group-hover:text-white transition-colors">{item.title}</h4>
                                            <div className="flex items-center gap-2 text-xs text-slate-500">
                                                <span>{item.subtitle}</span>
                                                <span className="w-1 h-1 rounded-full bg-slate-600"></span>
                                                <span>{timeAgo(item.date.toISOString())}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => {
                                            if (item.kind === 'report') navigate(`/reports/${item.id}`);
                                            else if (item.kind === 'workflow') navigate(`/workflow/${item.id}`);
                                            else if (item.kind === 'package') navigate(`/admin-packages/${item.id}`);
                                            else if (item.kind === 'quotation') navigate(`/quotations/${item.id}`);
                                        }}
                                        className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors opacity-0 group-hover:opacity-100"
                                    >
                                        <Eye size={18} />
                                    </button>
                                </div>
                            ))}
                        </div>

                        <div className="mt-6 pt-4 border-t border-slate-700/50">
                            <Pagination currentPage={currentPage} totalPages={Math.ceil(filteredItems.length / ITEMS_PER_PAGE)} onPageChange={setCurrentPage} />
                        </div>
                    </div>
                </div>

                {/* Side Widgets */}
                <div className="space-y-6">
                    {/* Quick Actions */}
                    <div className="glass-panel rounded-2xl p-6">
                        <h3 className="text-lg font-bold text-white mb-4">إجراءات سريعة</h3>
                        <div className="grid grid-cols-2 gap-3">
                            <button onClick={() => navigate('/employees')} className="p-4 rounded-xl bg-slate-800/50 hover:bg-blue-600/20 hover:border-blue-500/50 border border-slate-700/50 transition-all group text-center">
                                <Users size={24} className="mx-auto mb-2 text-blue-400 group-hover:scale-110 transition-transform" />
                                <span className="text-xs font-medium text-slate-300">الموظفين</span>
                            </button>
                            <button onClick={() => navigate('/admin-packages')} className="p-4 rounded-xl bg-slate-800/50 hover:bg-purple-600/20 hover:border-purple-500/50 border border-slate-700/50 transition-all group text-center">
                                <PackageIcon size={24} className="mx-auto mb-2 text-purple-400 group-hover:scale-110 transition-transform" />
                                <span className="text-xs font-medium text-slate-300">البكجات</span>
                            </button>
                            <button onClick={() => navigate('/admin-quotations')} className="p-4 rounded-xl bg-slate-800/50 hover:bg-emerald-600/20 hover:border-emerald-500/50 border border-slate-700/50 transition-all group text-center">
                                <DollarSign size={24} className="mx-auto mb-2 text-emerald-400 group-hover:scale-110 transition-transform" />
                                <span className="text-xs font-medium text-slate-300">العروض</span>
                            </button>
                            <button onClick={() => navigate('/workflow')} className="p-4 rounded-xl bg-slate-800/50 hover:bg-orange-600/20 hover:border-orange-500/50 border border-slate-700/50 transition-all group text-center">
                                <Download size={24} className="mx-auto mb-2 text-orange-400 group-hover:scale-110 transition-transform" />
                                <span className="text-xs font-medium text-slate-300">الاستيراد</span>
                            </button>
                        </div>
                    </div>

                    {/* System Status */}
                    <div className="glass-card rounded-2xl p-6 bg-gradient-to-br from-indigo-900/50 to-slate-900/50">
                        <h3 className="text-lg font-bold text-white mb-4">حالة النظام</h3>
                        <div className="space-y-4">
                            <div className="flex justify-between items-center text-sm">
                                <span className="text-slate-400">الخادم</span>
                                <span className="text-emerald-400 flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span> متصل</span>
                            </div>
                            <div className="flex justify-between items-center text-sm">
                                <span className="text-slate-400">قاعدة البيانات</span>
                                <span className="text-emerald-400 flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500"></span> متصل</span>
                            </div>
                            <div className="flex justify-between items-center text-sm">
                                <span className="text-slate-400">آخر نسخة احتياطية</span>
                                <span className="text-slate-300">منذ 2 ساعة</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ModernAdminDashboardScreen;
